import * as React from "react";
import { CrudPage, CrudResource } from "../components/CrudPage";
import { Badge } from "../components/Badge";
import { Banner } from "../components/States";
import { useAsync } from "../general/useAsync";
import { usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import * as Structure from "../structure/StructureStore";
import * as Store from "./ManagementStore";

/**
 * The offering: academic years, cycles, programmes, program levels, qualification modules and
 * teaching groups.
 *
 * These six screens replace the scaffolded Razor CRUD under Pages/Admin/Assessment/**, which the old
 * React app linked out to rather than implementing.
 */

const useCanWrite = () => usePermission(Permissions.MANAGEMENT);

const dateCell = (value?: Date) => (value ? new Date(value).toLocaleDateString() : <span className="text-text-weak">&mdash;</span>);

interface DatedDraft {
    name: string;
    startDate: string;
    endDate: string;
}

// -------------------------------------------------------------- academic years

export const AcademicYearsScreen: React.FC = () => {
    const canWrite = useCanWrite();

    const resource: CrudResource<Store.AcademicYearAPIModel, DatedDraft> = {
        title: "Academic years",
        icon: ["fad", "calendar"],
        description: "The years the institution runs. Assessment cycles sit inside a year.",
        singular: "Academic year",
        plural: "Academic years",
        list: Store.listAcademicYears,
        create: (d) =>
            Store.createAcademicYear({ name: d.name, startDate: Store.fromDateInput(d.startDate), endDate: Store.fromDateInput(d.endDate) }),
        update: (id, d) =>
            Store.updateAcademicYear({
                id,
                name: d.name,
                startDate: Store.fromDateInput(d.startDate),
                endDate: Store.fromDateInput(d.endDate),
            }),
        remove: Store.deleteAcademicYear,
        idOf: (y) => y.id ?? "",
        labelOf: (y) => y.name ?? "",
        columns: [
            { header: "Name", render: (y) => <span className="font-medium text-text-strong">{y.name}</span> },
            { header: "Starts", render: (y) => dateCell(y.startDate) },
            { header: "Ends", render: (y) => dateCell(y.endDate) },
            { header: "Cycles", align: "right", render: (y) => y.assessmentCycles?.length ?? 0 },
        ],
        fields: [
            { name: "name", label: "Name", required: true, placeholder: "2026" },
            { name: "startDate", label: "Start date", type: "date" },
            { name: "endDate", label: "End date", type: "date" },
        ],
        toDraft: (y) => ({
            name: y?.name ?? "",
            startDate: Store.toDateInput(y?.startDate),
            endDate: Store.toDateInput(y?.endDate),
        }),
        canWrite,
    };

    return <CrudPage resource={resource} />;
};

// ----------------------------------------------------------- assessment cycles

interface CycleDraft extends DatedDraft {
    rank: string;
    academicYearID: string;
}

export const AssessmentCyclesScreen: React.FC = () => {
    const canWrite = useCanWrite();
    const years = useAsync(() => Store.listAcademicYears(), [], { initial: [] });

    const yearOptions = React.useMemo(
        () => (years.data ?? []).map((y) => ({ value: y.id ?? "", label: y.name ?? "(unnamed)" })),
        [years.data]
    );

    const resource: CrudResource<Store.AssessmentCycleAPIModel, CycleDraft> = {
        title: "Assessment cycles",
        icon: ["fad", "calendar-week"],
        description: "Terms or semesters inside an academic year.",
        singular: "Assessment cycle",
        plural: "Assessment cycles",
        list: Store.listAssessmentCycles,
        create: (d) =>
            Store.createAssessmentCycle({
                name: d.name,
                rank: Number(d.rank || 0),
                academicYearID: d.academicYearID || undefined,
                startDate: Store.fromDateInput(d.startDate),
                endDate: Store.fromDateInput(d.endDate),
            }),
        update: (id, d) =>
            Store.updateAssessmentCycle({
                id,
                name: d.name,
                rank: Number(d.rank || 0),
                academicYearID: d.academicYearID || undefined,
                startDate: Store.fromDateInput(d.startDate),
                endDate: Store.fromDateInput(d.endDate),
            }),
        remove: Store.deleteAssessmentCycle,
        idOf: (c) => c.id ?? "",
        labelOf: (c) => c.name ?? "",
        searchOf: (c) => `${c.name ?? ""} ${c.academicYear?.name ?? ""}`,
        columns: [
            { header: "Name", render: (c) => <span className="font-medium text-text-strong">{c.name}</span> },
            { header: "Academic year", render: (c) => c.academicYear?.name || <span className="text-text-weak">&mdash;</span> },
            {
                header: "Rank",
                align: "right",
                render: (c) => (c.rank === -1 ? <Badge tone="info">Full year</Badge> : c.rank),
            },
            { header: "Starts", render: (c) => dateCell(c.startDate) },
            { header: "Ends", render: (c) => dateCell(c.endDate) },
        ],
        fields: [
            { name: "name", label: "Name", required: true, placeholder: "Term 1" },
            { name: "academicYearID", label: "Academic year", type: "select", options: yearOptions, placeholder: "Unassigned" },
            {
                name: "rank",
                label: "Rank",
                type: "number",
                hint: "Ordering within the year. 1-based here; -1 means the cycle spans the whole year. Legacy data is 0-based - do not mix the two.",
            },
            { name: "startDate", label: "Start date", type: "date" },
            { name: "endDate", label: "End date", type: "date" },
        ],
        toDraft: (c) => ({
            name: c?.name ?? "",
            rank: c?.rank === undefined ? "1" : String(c.rank),
            academicYearID: c?.academicYearID ?? "",
            startDate: Store.toDateInput(c?.startDate),
            endDate: Store.toDateInput(c?.endDate),
        }),
        canWrite,
    };

    return <CrudPage resource={resource} deps={[years.data]} />;
};

// ---------------------------------------------------------- academic programs

export const AcademicProgramsScreen: React.FC = () => {
    const canWrite = useCanWrite();

    const resource: CrudResource<Store.AcademicProgramAPIModel, DatedDraft> = {
        title: "Academic programs",
        icon: ["fad", "rectangle-list"],
        description: "Programme definitions, which can span more than one academic year.",
        singular: "Academic program",
        plural: "Academic programs",
        list: Store.listAcademicPrograms,
        create: (d) =>
            Store.createAcademicProgram({
                name: d.name,
                startDate: Store.fromDateInput(d.startDate),
                endDate: Store.fromDateInput(d.endDate),
            }),
        update: (id, d) =>
            Store.updateAcademicProgram({
                id,
                name: d.name,
                startDate: Store.fromDateInput(d.startDate),
                endDate: Store.fromDateInput(d.endDate),
            }),
        remove: Store.deleteAcademicProgram,
        idOf: (p) => p.id ?? "",
        labelOf: (p) => p.name ?? "",
        columns: [
            { header: "Name", render: (p) => <span className="font-medium text-text-strong">{p.name}</span> },
            { header: "Starts", render: (p) => dateCell(p.startDate) },
            { header: "Ends", render: (p) => dateCell(p.endDate) },
        ],
        fields: [
            { name: "name", label: "Name", required: true, placeholder: "Day school" },
            { name: "startDate", label: "Start date", type: "date" },
            { name: "endDate", label: "End date", type: "date" },
        ],
        toDraft: (p) => ({
            name: p?.name ?? "",
            startDate: Store.toDateInput(p?.startDate),
            endDate: Store.toDateInput(p?.endDate),
        }),
        canWrite,
    };

    return <CrudPage resource={resource} />;
};

// ------------------------------------------------------------- program levels

interface ProgramLevelDraft {
    qualificationID: string;
    qualificationLevelID: string;
    academicProgramID: string;
    academicYearID: string;
}

export const ProgramLevelsScreen: React.FC = () => {
    const canWrite = useCanWrite();

    const qualifications = useAsync(() => Structure.listQualifications(), [], { initial: [] });
    const levels = useAsync(() => Structure.listQualificationLevels(), [], { initial: [] });
    const programs = useAsync(() => Store.listAcademicPrograms(), [], { initial: [] });
    const years = useAsync(() => Store.listAcademicYears(), [], { initial: [] });

    const opts = <T extends { id?: string; name?: string }>(items?: T[]) =>
        (items ?? []).map((i) => ({ value: i.id ?? "", label: i.name ?? "(unnamed)" }));

    const resource: CrudResource<Store.QualificationProgramLevelAPIModel, ProgramLevelDraft> = {
        title: "Program levels",
        icon: ["fad", "layer-group"],
        description:
            "A qualification level, running under a particular programme in a particular year. This is the cohort a qualification module belongs to.",
        singular: "Program level",
        plural: "Program levels",
        list: Store.listQualificationProgramLevels,
        create: (d) =>
            Store.createQualificationProgramLevel({
                qualificationID: d.qualificationID || undefined,
                qualificationLevelID: d.qualificationLevelID || undefined,
                academicProgramID: d.academicProgramID || undefined,
                academicYearID: d.academicYearID || undefined,
            }),
        update: (id, d) =>
            Store.updateQualificationProgramLevel({
                id,
                qualificationID: d.qualificationID || undefined,
                qualificationLevelID: d.qualificationLevelID || undefined,
                academicProgramID: d.academicProgramID || undefined,
                academicYearID: d.academicYearID || undefined,
            }),
        remove: Store.deleteQualificationProgramLevel,
        idOf: (l) => l.id ?? "",
        labelOf: Store.programLevelLabel,
        searchOf: Store.programLevelLabel,
        columns: [
            { header: "Qualification", render: (l) => l.qualification?.name || <span className="text-text-weak">&mdash;</span> },
            {
                header: "Level",
                render: (l) => <span className="font-medium text-text-strong">{l.qualificationLevel?.name ?? "—"}</span>,
            },
            { header: "Programme", render: (l) => l.academicProgram?.name || <span className="text-text-weak">&mdash;</span> },
            { header: "Year", render: (l) => l.academicYear?.name || <span className="text-text-weak">&mdash;</span> },
            { header: "Modules", align: "right", render: (l) => l.modules?.length ?? 0 },
        ],
        fields: [
            { name: "qualificationID", label: "Qualification", type: "select", options: opts(qualifications.data), placeholder: "Select" },
            { name: "qualificationLevelID", label: "Level", type: "select", options: opts(levels.data), placeholder: "Select" },
            { name: "academicProgramID", label: "Programme", type: "select", options: opts(programs.data), placeholder: "Select" },
            { name: "academicYearID", label: "Academic year", type: "select", options: opts(years.data), placeholder: "Select" },
        ],
        toDraft: (l) => ({
            qualificationID: l?.qualificationID ?? "",
            qualificationLevelID: l?.qualificationLevelID ?? "",
            academicProgramID: l?.academicProgramID ?? "",
            academicYearID: l?.academicYearID ?? "",
        }),
        canWrite,
    };

    return <CrudPage resource={resource} deps={[qualifications.data, levels.data, programs.data, years.data]} />;
};

// -------------------------------------------------------- qualification modules

interface QualificationModuleDraft {
    qualificationProgramLevelID: string;
    moduleID: string;
    assessmentCycleID: string;
}

export const QualificationModulesScreen: React.FC = () => {
    const canWrite = useCanWrite();

    const levels = useAsync(() => Store.listQualificationProgramLevels(), [], { initial: [] });
    const modules = useAsync(() => Structure.listModules(), [], { initial: [] });
    const cycles = useAsync(() => Store.listAssessmentCycles(), [], { initial: [] });

    const resource: CrudResource<Store.QualificationModuleAPIModel, QualificationModuleDraft> = {
        title: "Qualification modules",
        icon: ["fad", "cubes"],
        description:
            "One subject, for one cohort, in one cycle. This is the pivot of the whole domain - assessment trees hang off it, procedure assignments bind to it, and the calculation engine targets it.",
        singular: "Qualification module",
        plural: "Qualification modules",
        list: Store.listQualificationModules,
        create: (d) =>
            Store.createQualificationModule({
                qualificationProgramLevelID: d.qualificationProgramLevelID || undefined,
                moduleID: d.moduleID || undefined,
                assessmentCycleID: d.assessmentCycleID || undefined,
            }),
        update: (id, d) =>
            Store.updateQualificationModule({
                id,
                qualificationProgramLevelID: d.qualificationProgramLevelID || undefined,
                moduleID: d.moduleID || undefined,
                assessmentCycleID: d.assessmentCycleID || undefined,
            }),
        remove: Store.deleteQualificationModule,
        idOf: (m) => m.id ?? "",
        labelOf: Store.qualificationModuleLabel,
        searchOf: Store.qualificationModuleLabel,
        columns: [
            { header: "Module", render: (m) => <span className="font-medium text-text-strong">{m.module?.name ?? "—"}</span> },
            {
                header: "Cohort",
                render: (m) => Store.programLevelLabel(m.qualificationProgramLevel) || <span className="text-text-weak">&mdash;</span>,
            },
            { header: "Cycle", render: (m) => m.assessmentCycle?.name || <span className="text-text-weak">&mdash;</span> },
        ],
        fields: [
            {
                name: "qualificationProgramLevelID",
                label: "Program level",
                type: "select",
                placeholder: "Select",
                options: (levels.data ?? []).map((l) => ({ value: l.id ?? "", label: Store.programLevelLabel(l) })),
            },
            {
                name: "moduleID",
                label: "Module",
                type: "select",
                placeholder: "Select",
                options: (modules.data ?? []).map((m) => ({ value: m.id ?? "", label: m.name ?? "(unnamed)" })),
            },
            {
                name: "assessmentCycleID",
                label: "Assessment cycle",
                type: "select",
                placeholder: "Select",
                options: (cycles.data ?? []).map((c) => ({ value: c.id ?? "", label: c.name ?? "(unnamed)" })),
            },
        ],
        toDraft: (m) => ({
            qualificationProgramLevelID: m?.qualificationProgramLevelID ?? "",
            moduleID: m?.moduleID ?? "",
            assessmentCycleID: m?.assessmentCycleID ?? "",
        }),
        canWrite,
    };

    return (
        <CrudPage resource={resource} deps={[levels.data, modules.data, cycles.data]}>
            <Banner tone="info" className="mb-4">
                The combination of program level, module and cycle is unique. Creating one that already exists is rejected by the
                database, not just by this form.
            </Banner>
        </CrudPage>
    );
};

// ------------------------------------------------------------- teaching groups

interface TeachingGroupDraft {
    name: string;
    description: string;
    qualificationModuleID: string;
}

export const TeachingGroupsScreen: React.FC = () => {
    const canWrite = useCanWrite();
    const modules = useAsync(() => Store.listQualificationModules(), [], { initial: [] });

    const resource: CrudResource<Store.TeachingGroupAPIModel, TeachingGroupDraft> = {
        title: "Teaching groups",
        icon: ["fad", "users"],
        description:
            "Class and register groups within a qualification module. A roster filter for mark entry and reports - not something the calculation engine targets.",
        singular: "Teaching group",
        plural: "Teaching groups",
        list: Store.listTeachingGroups,
        create: (d) =>
            Store.createTeachingGroup({
                name: d.name,
                description: d.description,
                qualificationModuleID: d.qualificationModuleID || undefined,
            }),
        update: (id, d) =>
            Store.updateTeachingGroup({
                id,
                name: d.name,
                description: d.description,
                qualificationModuleID: d.qualificationModuleID || undefined,
            }),
        remove: Store.deleteTeachingGroup,
        idOf: (g) => g.id ?? "",
        labelOf: (g) => g.name ?? "",
        searchOf: (g) => `${g.name ?? ""} ${g.description ?? ""} ${Store.qualificationModuleLabel(g.module)}`,
        columns: [
            { header: "Name", render: (g) => <span className="font-medium text-text-strong">{g.name}</span> },
            {
                header: "Qualification module",
                render: (g) => Store.qualificationModuleLabel(g.module) || <span className="text-text-weak">&mdash;</span>,
            },
            { header: "Description", render: (g) => g.description || <span className="text-text-weak">&mdash;</span> },
        ],
        fields: [
            { name: "name", label: "Name", required: true, placeholder: "10A" },
            {
                name: "qualificationModuleID",
                label: "Qualification module",
                type: "select",
                placeholder: "Select",
                options: (modules.data ?? []).map((m) => ({ value: m.id ?? "", label: Store.qualificationModuleLabel(m) })),
            },
            { name: "description", label: "Description", type: "textarea" },
        ],
        toDraft: (g) => ({
            name: g?.name ?? "",
            description: g?.description ?? "",
            qualificationModuleID: g?.qualificationModuleID ?? "",
        }),
        canWrite,
    };

    return <CrudPage resource={resource} deps={[modules.data]} />;
};
